import { useState, useEffect, useCallback, useRef } from "react";
import "@/App.css";
import axios from "axios";
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import {
  Radio, Settings, Info, X, EyeOff,
  Volume2, VolumeX, Signal,
} from "lucide-react";
import { Toaster, toast } from "sonner";

import { LoadingSequence } from "./components/LoadingSequence";
import { TrackerPanel } from "./components/TrackerPanel";
import { ActivityPanel } from "./components/ActivityPanel";
import { EmoteBar, EMOTES, getEmoteIcon } from "./components/EmoteBar";
import { TraceTracker } from "./components/TraceTracker";
import { Crosshair } from "lucide-react";
import { renderToStaticMarkup } from "react-dom/server";

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
const API = `${BACKEND_URL}/api`;

const getWatcherId = () => {
  try {
    let id = localStorage.getItem("tracker_watcher_id");
    if (!id) {
      id = (crypto && crypto.randomUUID && crypto.randomUUID()) ||
           `w-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem("tracker_watcher_id", id);
    }
    return id;
  } catch {
    return `w-${Date.now()}`;
  }
};
const WATCHER_ID = getWatcherId();

const DEFAULT_LAT = -19.2590;
const DEFAULT_LNG = 146.8169;

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

// --- Icons ----------------------------------------------------------------

const createUserIcon = () =>
  L.divIcon({
    className: "user-marker",
    html: `<div class="user-marker-inner"></div>`,
    iconSize: [12, 12],
    iconAnchor: [6, 6],
  });

const createAgentIcon = (id, code, isFlying, restoredPulse) =>
  L.divIcon({
    className: `vehicle-marker agent-marker-live${isFlying ? " flying" : ""}${restoredPulse ? " signal-restored" : ""}`,
    html: `
      <div class="agent-marker-inner" data-testid="agent-marker-${id}">
        <div class="agent-marker-ring"></div>
        <div class="agent-marker-dot"></div>
        <div class="agent-marker-code">${code}</div>
      </div>
    `,
    iconSize: [64, 48],
    iconAnchor: [32, 24],
  });

const createFootstepIcon = (index) =>
  L.divIcon({
    className: "footstep-marker",
    html: `<div class="footstep-inner" data-testid="footstep-${index}"><svg viewBox="0 0 24 24" width="14" height="14" fill="rgba(255,255,255,0.78)" stroke="none">
      <path d="M7 2c1.1 0 2 .9 2 2 0 1.5-1 4-2 4s-2-2.5-2-4c0-1.1.9-2 2-2zm10 2c1.1 0 2 .9 2 2 0 1.5-1 4-2 4s-2-2.5-2-4c0-1.1.9-2 2-2zM5.5 12c1.5 0 3 1 3 3s-1 4-3 4-3-2.5-3-4 1.5-3 3-3zm12 2c1.5 0 3 1 3 3s-1 4-3 4-3-2.5-3-4 1.5-3 3-3z"/>
    </svg></div>`,
    iconSize: [14, 14],
    iconAnchor: [7, 7],
  });

const _emoteIconCache = new Map();
const createEmoteIcon = (emoteId) => {
  if (_emoteIconCache.has(emoteId)) return _emoteIconCache.get(emoteId);
  const meta = getEmoteIcon(emoteId);
  if (!meta) return null;
  const { Icon } = meta;
  // Render the lucide icon to static HTML so Leaflet can use it inside a divIcon
  const svg = renderToStaticMarkup(<Icon size={20} strokeWidth={1.8} />);
  const icon = L.divIcon({
    className: "emote-marker",
    html: `<div class="emote-marker-inner" data-testid="emote-marker-${emoteId}">${svg}</div>`,
    iconSize: [44, 44],
    iconAnchor: [22, 22],
  });
  _emoteIconCache.set(emoteId, icon);
  return icon;
};

// --- Helpers --------------------------------------------------------------

const formatTime = (iso) => {
  if (!iso) return "--:--:--";
  try { return new Date(iso).toLocaleTimeString("en-AU", { hour12: false, timeZone: "Australia/Brisbane" }); }
  catch { return iso; }
};

const MapController = ({ flyTo, followLocation }) => {
  const map = useMap();
  const lastFollow = useRef(null);

  useEffect(() => {
    if (flyTo) map.flyTo([flyTo.lat, flyTo.lng], 14, { duration: 1.5 });
  }, [flyTo, map]);

  useEffect(() => {
    if (!followLocation) { lastFollow.current = null; return; }
    const last = lastFollow.current;
    if (!last ||
        Math.abs(last.lat - followLocation.lat) > 1e-6 ||
        Math.abs(last.lng - followLocation.lng) > 1e-6) {
      map.panTo([followLocation.lat, followLocation.lng], { animate: true, duration: 0.8 });
      lastFollow.current = followLocation;
    }
  }, [followLocation, map]);

  return null;
};

/**
 * Small red arrows pinned to the viewport edge for any emote that's
 * currently active but outside the visible map bounds. Recomputes on
 * every map move/zoom and every emote list change.
 */
const EmoteEdgeIndicators = ({ emotes }) => {
  const map = useMap();
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!map) return undefined;
    const bump = () => setTick((t) => t + 1);
    map.on("move zoom moveend zoomend", bump);
    return () => map.off("move zoom moveend zoomend", bump);
  }, [map]);
  // tick just forces a re-render on map events
  void tick;

  if (!map || !emotes || emotes.length === 0) return null;
  const bounds = map.getBounds();
  const size = map.getSize();          // {x, y} pixel size of the map container
  const margin = 26;                   // keep arrows this far from the edge
  const cx = size.x / 2;
  const cy = size.y / 2;
  const halfW = cx - margin;
  const halfH = cy - margin;

  const offscreen = emotes.filter((e) => !bounds.contains([e.lat, e.lng]));
  if (offscreen.length === 0) return null;

  const arrows = offscreen.map((e) => {
    const p = map.latLngToContainerPoint([e.lat, e.lng]);
    const dx = p.x - cx;
    const dy = p.y - cy;
    // Scale so the arrow sits on the edge box (whichever axis clips first)
    const scale = Math.min(halfW / Math.max(1, Math.abs(dx)), halfH / Math.max(1, Math.abs(dy)));
    const x = cx + dx * scale;
    const y = cy + dy * scale;
    const angleDeg = Math.atan2(dy, dx) * 180 / Math.PI; // 0 = right
    return { id: e.id, x, y, angleDeg, emoji_id: e.emoji_id };
  });

  return (
    <div className="emote-edge-layer" data-testid="emote-edge-layer">
      {arrows.map((a) => (
        <div
          key={a.id}
          className="emote-edge-arrow"
          data-testid={`emote-edge-${a.emoji_id}`}
          style={{
            left: `${a.x}px`,
            top: `${a.y}px`,
            transform: `translate(-50%, -50%) rotate(${a.angleDeg}deg)`,
          }}
          title={`Emote off-screen`}
        >
          <span className="emote-edge-arrow-head" />
        </div>
      ))}
    </div>
  );
};

function generateFootsteps(center) {
  const pts = [];
  const seed = Math.floor(center.lat * 1000) + Math.floor(center.lng * 1000);
  let rnd = seed;
  const pseudo = () => { rnd = (rnd * 9301 + 49297) % 233280; return rnd / 233280; };
  for (let i = 0; i < 6; i++) {
    const angle = pseudo() * 2 * Math.PI;
    const dist = 150 + pseudo() * 300;
    const lat = center.lat + (dist / 111000) * Math.cos(angle);
    const lng = center.lng + (dist / 111000) * Math.sin(angle) / Math.cos(center.lat * Math.PI / 180);
    pts.push({ lat, lng });
  }
  return pts;
}

// --- Main App -------------------------------------------------------------

function App() {
  const [bootPhase, setBootPhase] = useState("loading");
  const [agent, setAgent] = useState(null);
  const [activities, setActivities] = useState([]);
  const [userLocation, setUserLocation] = useState(null);
  const [serverTime, setServerTime] = useState(null);
  const [now, setNow] = useState(new Date());
  const [watchersOnline, setWatchersOnline] = useState(0);

  const [showSettings, setShowSettings] = useState(false);
  const [privacyMode, setPrivacyMode] = useState(false);
  const [showPrivacyInfo, setShowPrivacyInfo] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [trackedOpen, setTrackedOpen] = useState(true);
  const [activityOpen, setActivityOpen] = useState(false);
  const [traceCount, setTraceCount] = useState(0);
  const [traceScreenOpen, setTraceScreenOpen] = useState(false);

  const [flyTo, setFlyTo] = useState(null);
  const [follow, setFollow] = useState(false);
  const audioCtxRef = useRef(null);
  const prevAgentIdRef = useRef(null);
  const prevSignalSearchingRef = useRef(null);
  const [signalRestoredPulse, setSignalRestoredPulse] = useState(false);
  const sessionId = WATCHER_ID.slice(-6).toUpperCase();

  // Smooth client-side tweening between server polls
  const [displayLocation, setDisplayLocation] = useState(null);
  const tweenRef = useRef({ from: null, to: null, startAt: 0, durationMs: 2000, agentId: null });

  // Active emotes broadcast by other watchers
  const [emotes, setEmotes] = useState([]);
  const seenEmoteIds = useRef(new Set());

  const finishLoading = useCallback(() => setBootPhase("live"), []);

  useEffect(() => { const t = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(t); }, []);

  const playBlip = useCallback(() => {
    if (!soundEnabled) return;
    try {
      if (!audioCtxRef.current) {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return;
        audioCtxRef.current = new Ctx();
      }
      const ctx = audioCtxRef.current;
      if (ctx.state === "suspended") ctx.resume();
      const t = ctx.currentTime;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.setValueAtTime(740, t);
      osc.frequency.exponentialRampToValueAtTime(1200, t + 0.08);
      gain.gain.setValueAtTime(0.0001, t);
      gain.gain.exponentialRampToValueAtTime(0.12, t + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.25);
      osc.connect(gain); gain.connect(ctx.destination);
      osc.start(t); osc.stop(t + 0.3);
    } catch {}
  }, [soundEnabled]);

  // Shorter, higher-pitched click for incoming emotes — distinct from the
  // agent-handoff blip so users learn the difference.
  const playEmoteBlip = useCallback(() => {
    if (!soundEnabled) return;
    try {
      if (!audioCtxRef.current) {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return;
        audioCtxRef.current = new Ctx();
      }
      const ctx = audioCtxRef.current;
      if (ctx.state === "suspended") ctx.resume();
      const t = ctx.currentTime;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "triangle";
      osc.frequency.setValueAtTime(1600, t);
      osc.frequency.exponentialRampToValueAtTime(2000, t + 0.05);
      gain.gain.setValueAtTime(0.0001, t);
      gain.gain.exponentialRampToValueAtTime(0.06, t + 0.015);
      gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.12);
      osc.connect(gain); gain.connect(ctx.destination);
      osc.start(t); osc.stop(t + 0.15);
    } catch {}
  }, [soundEnabled]);

  const fetchState = useCallback(async () => {
    try {
      const [aRes, logRes, emRes, tcRes] = await Promise.all([
        axios.get(`${API}/active-agent`),
        axios.get(`${API}/activity-log?limit=10`),
        axios.get(`${API}/emotes`),
        axios.get(`${API}/traces/count`),
      ]);
      const newAgent = aRes.data.agent;
      setAgent(newAgent);
      setServerTime(aRes.data.server_timestamp);
      setWatchersOnline(aRes.data.watchers_online ?? 0);
      setActivities(logRes.data.activities || []);
      setTraceCount(tcRes.data.count ?? 0);

      // Emote handling: toast + short chirp each newly-seen emote, then update state
      const incoming = emRes.data.emotes || [];
      let newCount = 0;
      incoming.forEach((e) => {
        if (!seenEmoteIds.current.has(e.id)) {
          seenEmoteIds.current.add(e.id);
          newCount += 1;
          const meta = getEmoteIcon(e.emoji_id);
          const label = meta ? meta.label : e.emoji_id;
          toast(`Someone in ${e.region} sent a ${label}`, { duration: 3500 });
        }
      });
      if (newCount > 0) playEmoteBlip();
      setEmotes(incoming);

      if (newAgent) {
        const tw = tweenRef.current;
        const snap = !tw.to || tw.agentId !== newAgent.id;
        tweenRef.current = {
          from: snap ? newAgent.location : (tw.current || tw.to),
          to: newAgent.location,
          startAt: Date.now(),
          durationMs: snap ? 0 : 2000,
          agentId: newAgent.id,
          current: snap ? newAgent.location : (tw.current || tw.to),
        };
        if (snap) setDisplayLocation(newAgent.location);
      }

      if (newAgent && prevAgentIdRef.current && newAgent.id !== prevAgentIdRef.current) {
        playBlip();
        toast.success(`SIGNAL ACQUIRED · ${newAgent.code}`, {
          description: `Tracking from ${newAgent.base_name}`,
        });
      }

      // "Signal restored" — flip from signal_searching=true back to false
      // on the same agent. Skip on the very first update and on handoff.
      if (newAgent && prevSignalSearchingRef.current === true && !newAgent.signal_searching) {
        toast.success(`SIGNAL RESTORED · ${newAgent.code}`, {
          description: `Route locked · ${newAgent.base_name}`,
        });
        playBlip();
        setSignalRestoredPulse(true);
        setTimeout(() => setSignalRestoredPulse(false), 2500);
      }
      prevSignalSearchingRef.current = newAgent ? !!newAgent.signal_searching : null;
      prevAgentIdRef.current = newAgent ? newAgent.id : null;
    } catch (e) { /* non-fatal */ }
  }, [playBlip, playEmoteBlip]);

  const sendEmote = useCallback(async (emoteId) => {
    try {
      const body = {
        watcher_id: WATCHER_ID,
        emoji_id: emoteId,
        ...(userLocation && !privacyMode
          ? { lat: userLocation.lat, lng: userLocation.lng }
          : {}),
      };
      const res = await axios.post(`${API}/emote`, body);
      // Optimistically push our own emote into local state so we see it instantly
      if (res.data && res.data.success && res.data.emote) {
        const e = res.data.emote;
        seenEmoteIds.current.add(e.id);  // suppress duplicate toast on next poll
        setEmotes((prev) => [e, ...prev]);
        // Make sure the sender can see their own emote — fly the map to it.
        // (without this, a panned/zoomed/following map can leave it off-screen.)
        setFlyTo({ lat: e.lat, lng: e.lng });
      }
      return res.data;
    } catch (err) {
      return { success: false, reason: "network" };
    }
  }, [userLocation, privacyMode]);

  const updateWatcherBackend = useCallback(async (lat, lng) => {
    if (privacyMode) return;
    try { await axios.post(`${API}/update-user-location`, { lat, lng, watcher_id: WATCHER_ID }); } catch {}
  }, [privacyMode]);

  useEffect(() => {
    if (!("geolocation" in navigator)) { setUserLocation({ lat: DEFAULT_LAT, lng: DEFAULT_LNG }); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => { setUserLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude }); },
      () => { setUserLocation({ lat: DEFAULT_LAT, lng: DEFAULT_LNG }); },
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 0 }
    );
    const id = navigator.geolocation.watchPosition(
      (pos) => { setUserLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude }); },
      null,
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
    return () => navigator.geolocation.clearWatch(id);
  }, []);

  useEffect(() => {
    if (!userLocation) return;
    updateWatcherBackend(userLocation.lat, userLocation.lng);
  }, [userLocation, updateWatcherBackend]);

  useEffect(() => {
    if (privacyMode || !userLocation) return;
    const id = setInterval(() => updateWatcherBackend(userLocation.lat, userLocation.lng), 30000);
    return () => clearInterval(id);
  }, [privacyMode, userLocation, updateWatcherBackend]);

  useEffect(() => {
    (async () => {
      try {
        if (privacyMode) await axios.post(`${API}/clear-user-location`, { lat: 0, lng: 0, watcher_id: WATCHER_ID });
        else if (userLocation) updateWatcherBackend(userLocation.lat, userLocation.lng);
      } catch {}
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [privacyMode]);

  useEffect(() => {
    const tick = setInterval(() => {
      const tw = tweenRef.current;
      if (!tw.from || !tw.to) return;
      if (tw.durationMs <= 0) {
        tw.current = tw.to;
        setDisplayLocation(tw.to);
        return;
      }
      const t = Math.min(1, (Date.now() - tw.startAt) / tw.durationMs);
      const eased = 1 - (1 - t) * (1 - t);
      const loc = {
        lat: tw.from.lat + eased * (tw.to.lat - tw.from.lat),
        lng: tw.from.lng + eased * (tw.to.lng - tw.from.lng),
      };
      tw.current = loc;
      setDisplayLocation(loc);
    }, 33);
    return () => clearInterval(tick);
  }, []);

  const mapCenter = userLocation
    ? [userLocation.lat, userLocation.lng]
    : [DEFAULT_LAT, DEFAULT_LNG];

  useEffect(() => {
    fetchState();
    const id = setInterval(fetchState, 2000);
    return () => clearInterval(id);
  }, [fetchState]);

  // Client-side emote expiry — drop emotes whose 5s window has passed even
  // between polls. Runs at 4 Hz so the marker disappears promptly.
  useEffect(() => {
    if (emotes.length === 0) return undefined;
    const id = setInterval(() => {
      const now = Date.now() / 1000;
      setEmotes((prev) => prev.filter((e) => e.expires_ts > now));
    }, 250);
    return () => clearInterval(id);
  }, [emotes.length]);

  const showLoader = bootPhase === "loading";
  const agentLoc = agent ? (displayLocation || agent.location) : null;
  const followLocation = follow && agentLoc ? agentLoc : null;
  const showPOIs = agent && agent.status === "idle";
  const showFootsteps = agent && agent.signal_searching;

  return (
    <div className="app-container" data-testid="live-ui">
      <Toaster position="top-center" theme="dark" />

      <div className="top-bar">
        <div className="top-bar-left">
          <div className="logo">
            <Radio size={14} />
            <span>TRACKER</span>
          </div>
          <div className="time-display" data-testid="time-display">
            {serverTime ? formatTime(serverTime) : "--:--:--"} AEST
          </div>
        </div>
        <div className="top-bar-right">
          <div className="watchers-badge" data-testid="watchers-online">
            <Signal size={12} /> {watchersOnline} ONLINE
          </div>
          <button className="settings-btn" onClick={() => setShowSettings(!showSettings)} data-testid="settings-btn">
            <Settings size={14} />
          </button>
        </div>
      </div>

      {showSettings && (
        <div className="settings-panel" data-testid="settings-panel">
          <div className="settings-header">
            <span>SETTINGS</span>
            <button onClick={() => setShowSettings(false)}><X size={12} /></button>
          </div>
          <div className="settings-option">
            <div className="option-row">
              <EyeOff size={16} />
              <span>Privacy Mode</span>
              <button className="info-btn" onClick={() => setShowPrivacyInfo(!showPrivacyInfo)}><Info size={12} /></button>
            </div>
            <label className="toggle-switch">
              <input type="checkbox" checked={privacyMode} onChange={(e) => setPrivacyMode(e.target.checked)} data-testid="privacy-toggle" />
              <span className="toggle-slider"></span>
            </label>
          </div>
          {showPrivacyInfo && (
            <div className="privacy-info">
              Hides your location on the map and stops sharing your coordinates. Agents will no longer react to your presence.
            </div>
          )}
          <div className="settings-option">
            <div className="option-row">
              {soundEnabled ? <Volume2 size={16} /> : <VolumeX size={16} />}
              <span>Signal Alerts</span>
            </div>
            <label className="toggle-switch">
              <input type="checkbox" checked={soundEnabled} onChange={(e) => setSoundEnabled(e.target.checked)} data-testid="sound-toggle" />
              <span className="toggle-slider"></span>
            </label>
          </div>
        </div>
      )}

      <div className="map-container" data-testid="map-container">
        <MapContainer
          center={mapCenter}
          zoom={13}
          scrollWheelZoom
          className="leaflet-dark"
          zoomControl={false}
        >
          <TileLayer
            attribution='&copy; OpenStreetMap contributors &copy; CARTO'
            url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
          />
          <MapController flyTo={flyTo} followLocation={followLocation} />
          <EmoteEdgeIndicators emotes={emotes} />

          {userLocation && !privacyMode && (
            <Marker position={[userLocation.lat, userLocation.lng]} icon={createUserIcon()}>
              <Popup><div className="popup-content"><strong>YOUR LOCATION</strong></div></Popup>
            </Marker>
          )}

          {showPOIs && (agent.pois_near_current || []).map((p) => (
            <Marker
              key={`poi-${p.id}`}
              position={[p.location.lat, p.location.lng]}
              icon={L.divIcon({
                className: "poi-marker",
                html: `<div class="poi-dot ${p.type}"></div>`,
                iconSize: [8, 8],
                iconAnchor: [4, 4],
              })}
            >
              <Popup>
                <div className="popup-content">
                  <strong>{p.name}</strong>
                  <span className="popup-sub">{p.type.replace(/_/g, " ")}</span>
                </div>
              </Popup>
            </Marker>
          ))}

          {showFootsteps && agentLoc && (
            <>
              <Circle
                center={[agentLoc.lat, agentLoc.lng]}
                radius={500}
                pathOptions={{
                  color: "#ff2d2d",
                  weight: 1,
                  opacity: 0.7,
                  fillColor: "#ff2d2d",
                  fillOpacity: 0.07,
                  className: "signal-searching-ring",
                }}
              />
              {generateFootsteps(agentLoc).map((f, i) => (
                <Marker
                  key={`fs-${i}`}
                  position={[f.lat, f.lng]}
                  icon={createFootstepIcon(i)}
                >
                  <Popup>
                    <div className="popup-content popup-searching">
                      <span className="popup-searching-msg">Footprints detected — possible pass-through</span>
                    </div>
                  </Popup>
                </Marker>
              ))}
            </>
          )}

          {agent && !showFootsteps && (
            <Marker
              position={[agentLoc.lat, agentLoc.lng]}
              icon={createAgentIcon(agent.id, agent.code, agent.status === "moving" && agent.speed > 60, signalRestoredPulse)}
            >
              <Popup>
                <div className="popup-content">
                  <strong className="text-red">{agent.code}</strong>
                  <span className="popup-status">{agent.status.toUpperCase()}</span>
                  <span className="popup-speed">{agent.speed.toFixed(1)} km/h</span>
                </div>
              </Popup>
            </Marker>
          )}

          {/* Active emotes from any watcher (fuzzed location, 5s lifetime) */}
          {emotes.map((e) => {
            const icon = createEmoteIcon(e.emoji_id);
            if (!icon) return null;
            const meta = getEmoteIcon(e.emoji_id);
            return (
              <Marker
                key={e.id}
                position={[e.lat, e.lng]}
                icon={icon}
              >
                <Popup>
                  <div className="popup-content">
                    <strong>Someone in {e.region}</strong>
                    <span className="popup-sub">sent a {meta ? meta.label : e.emoji_id}</span>
                  </div>
                </Popup>
              </Marker>
            );
          })}
        </MapContainer>
      </div>

      <TrackerPanel
        agent={agent}
        now={now}
        open={trackedOpen}
        onToggle={() => setTrackedOpen(!trackedOpen)}
        follow={follow}
        setFollow={setFollow}
        setFlyTo={setFlyTo}
      />

      <ActivityPanel
        activities={activities}
        now={now}
        open={activityOpen}
        onToggle={() => setActivityOpen(!activityOpen)}
      />

      {/* Floating TRACES tab on the right edge, below the ACTIVITY tab */}
      <button
        className="trace-tab"
        onClick={() => setTraceScreenOpen(true)}
        data-testid="trace-tab"
        title="Open Trace Tracker"
      >
        <Crosshair size={12} />
        <span className="trace-tab-label">TRACES</span>
        <span className="trace-tab-count" data-testid="trace-tab-count">{traceCount}</span>
      </button>

      <TraceTracker
        open={traceScreenOpen}
        onClose={() => setTraceScreenOpen(false)}
        userLocation={userLocation}
        watcherId={WATCHER_ID}
      />

      <EmoteBar
        watcherId={WATCHER_ID}
        cooldownSeconds={8}
        onSent={sendEmote}
        activeCount={emotes.length}
      />

      {showLoader && <LoadingSequence onComplete={finishLoading} sessionId={sessionId} />}
    </div>
  );
}

export default App;
